import {Notify} from "@/shared/ui/notify";
import {Temporal} from "@/shared/utils/datefunctions";

import {ElementSelector, saveJQuerySelector} from "@/shared/utils/misc";

export type FeedbackType = "has-error" | "has-warning" | "has-success";

export function disable(input: ElementSelector) {
	saveJQuerySelector(input).prop("disabled", true);
}

export function enable(input: ElementSelector) {
	saveJQuerySelector(input).prop("disabled", false);
}

export function toggle(input: ElementSelector, enabled?: boolean) {
	const selector = saveJQuerySelector(input);
	selector.prop("disabled", !(enabled ?? selector.is(":disabled")));
}

export function disableByClass(input: ElementSelector) {
	saveJQuerySelector(input).addClass("disabled");
}

export function enableByClass(input: ElementSelector) {
	saveJQuerySelector(input).removeClass("disabled");
}

export function toggleByClass(input: ElementSelector, enabled?: boolean) {
	const selector = saveJQuerySelector(input);
	selector.toggleClass("disabled", !(enabled ?? selector.hasClass("disabled")));
}

export function toggleCheckbox(checkbox: ElementSelector, disabled: boolean, uncheckIfDisabled = false) {
	const selector = saveJQuerySelector(checkbox);
	selector.prop("disabled", disabled)
		.prop("checked", uncheckIfDisabled && disabled ? false : selector.is(":checked"));
}

export function addFeedback(input: ElementSelector, feedbackType: FeedbackType, message?: string) {
	const selector = saveJQuerySelector(input);
	const formGroup = selector.closest(".form-group, .form-group-custom-field");

	formGroup.addClass("has-feedback " + feedbackType);

	if (message != null) {
		let messageAnchor = selector;

		if (selector.parent(".input-group").length) {
			messageAnchor = selector.parent(".input-group");
		}

		messageAnchor.after($("<span></span>").addClass("help-block").text(message));
	}
}

/**
 * Adds a short error marker to an input field, marked with a standard error label below the field
 * with a title containing the full list of error messages (separated by commas)
 * @param input
 * @param feedbackType
 * @param errorMessages
 */
export function addShortFeedback(input: ElementSelector, feedbackType: FeedbackType, errorMessages?: string[]) {
	const selector = saveJQuerySelector(input);
	const formGroup = selector.closest(".form-group, .form-group-custom-field");

	formGroup.addClass("has-feedback " + feedbackType);

	if (errorMessages != null) {
		let messageAnchor = selector;

		if (selector.parent(".input-group").length) {
			messageAnchor = selector.parent(".input-group");
		}

		const separatedMessages = errorMessages.join(", ");
		messageAnchor.after(
			$("<span></span>")
				.addClass("help-block no-margin-bottom cursor-default")
				.prop("title", separatedMessages)
				.text(messages["form.error.feedback.short"])
				.prepend(
					$("<i></i>")
						.addClass("far fa-exclamation-triangle position-left")
						.prop("aria-hidden", true)
						.prop("title", separatedMessages))
		);
	}
}

export function removeFeedback(input: ElementSelector) {
	const selector = saveJQuerySelector(input);
	const formGroup = selector.closest(".form-group, .form-group-custom-field");
	formGroup.removeClass("has-feedback has-error has-warning has-success");

	selector.each((index, elem) => {
		let element = $(elem);
		if (element.parent(".input-group").length) {
			element = element.parent(".input-group");
		}
		element.nextAll("span.help-block").remove();
	});
}

export function addCopyButton(input: ElementSelector) {
	const selector = saveJQuerySelector(input);
	selector.each(function (this: HTMLElement) {
		const element = $(this);
		element.wrap(`<div class="input-group"></div>`);

		const copyButton = $(`<button type="button" class="btn btn-default" title="${messages["tooltip.copy"]}" data-toggle="tooltip"><i class="icon-copy3"></i></button>`);

		copyButton.click(async () => {
			// Browser compatibility: https://caniuse.com/mdn-api_clipboard_writetext
			// https://www.w3.org/TR/clipboard-apis/#dom-clipboard-writetext
			if (navigator.clipboard) {
				// clipboard only available in https context
				await navigator.clipboard.writeText(element.val() as string);
				const tooltipId = copyButton.attr("aria-describedby");
				if (tooltipId != null) {
					const tooltip = $("#" + tooltipId);
					tooltip.find(".tooltip-inner").html(messages["tooltip.copied"]);
				}
			} else {
				Notify.danger(messages["tooltip.copyNotSupported"]);
			}
		});

		copyButton.wrap(`<div class="input-group-btn"></div>`)
			.parent()
			.insertAfter(element);
	});
}

export function initSingleDateRangePickers(dateRangePickers: JQuery) {
	dateRangePickers.each((i, elem: HTMLElement) => {
		const dateRangePicker: JQuery = $(elem);
		dateRangePicker.daterangepicker({
			singleDatePicker: true,
			opens: "right",
			drops: dateRangePicker.data("drp-drops") as "down" | "up" | "auto" ?? "down",
			autoUpdateInput: false,
			locale: {
				format: globals.dateFormat,
				monthNames: globals.monthNames,
				daysOfWeek: globals.dayNamesShort,
				firstDay: globals.firstDayOfWeek
			}
		})
	});
	// DateRangePicker does not listen for 'change' event and just triggers on 'keyup'
	// this means that programmatically changing the value of the input element does not
	// update the dateRangePicker registered on the input element.
	// As a fix we check the values on picker 'show' and notify the picker.
	dateRangePickers.on("show.daterangepicker", function (this: HTMLElement, ev: JQuery.TriggeredEvent, picker: DateRangePicker.Picker) {
		const input = ev.target as HTMLInputElement;
		const datePickerValue = picker.startDate.format(globals.dateFormat);

		if (input.value !== datePickerValue) {
			picker.elementChanged();
		}

		$(this).on("keydown", function (e) {
			if ((e.keyCode === 27)) {
				picker.hide();
			}
		});
	});

	// if the dateRangePicker is applied, update the input value
	// this is necessary because the dateRangePicker does not update the input value on apply
	// for pickers with autoUpdateInput: false (TIMR-8311)
	dateRangePickers.on("apply.daterangepicker", (event: JQuery.TriggeredEvent, picker: DateRangePicker.Picker) => {
		$(event.target).val(picker.startDate.format(globals.dateFormat)).change();
	});

	/* for fields that opt out of daterangepicker autoUpdateInput, hide() no longer writes the internal picker date back into the input.
	 * recreate only the old fallback behavior for invalid manual input: if the user leaves an invalid date in the field and the picker closes,
	 * restore the current picker date instead of keeping the invalid text. This keeps manual invalid-date correction, but still avoids overwriting valid DOM values with stale picker state, see TIMR-11939.
	 */
	dateRangePickers.on("hide.daterangepicker", (event: JQuery.TriggeredEvent, picker: DateRangePicker.Picker) => {
		const input = $(event.target);

		// only restore the picker state for invalid manual input - if the field already contains a valid date, keep the current DOM value unchanged
		// strict=true prevents malformed partial matches (e.g. "a9.04.2026") from being treated as valid.
		if (moment(input.val(), picker.locale.format, true).isValid()) {
			return;
		}

		input.val(picker.startDate.format(picker.locale.format)).change();
	});

	// Init clear button for date pickers
	$("[data-clear-input-for]").click(e => {
		// DEVNOTE currentTarget is necessary to get the element to which the event listener is attached, otherwise it will not work when clicking on a child element
		const elementId: string = $(e.currentTarget).data("clear-input-for") as string;
		const inputElement = $("#" + elementId);
		inputElement.val("").change();
	});
}

export function getLastMonthEnd(): Temporal.PlainDate {
	return Temporal.Now.plainDateISO().with({day: 1}).subtract({days: 1});
}

/**
 * Toggle the visibility of the selector based on the switch value with a smooth animation effect
 */
export function slideToggle(selector: ElementSelector, swtch?: boolean, duration?: number, easing?: string, callback?: () => void) {
	const element = saveJQuerySelector(selector);
	return swtch ? element.slideDown(duration, easing, callback) : element.slideUp(duration, easing, callback);
}
